import { useContext, useEffect, useRef, useState } from 'react';

import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  AssignmentOutlined,
  FavoriteBorderOutlined
} from '@mui/icons-material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChangeHistoryOutlinedIcon from '@mui/icons-material/ChangeHistoryOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import {
  Box,
  Button,
  CardMedia,
  Chip,
  Divider,
  Grid,
  IconButton,
  MobileStepper,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material';
import { AxiosError } from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { response } from '../../../api/db';
import { getProductEvaluations } from '../../../api/db/evaluation';
import { addFavorite, deleteFavorite } from '../../../api/db/favorite';
import { getProduct } from '../../../api/db/product';
import { sideBarWidth, spHeaderHeight } from '../../../constants/Location';
import {
  gray10,
  gray100,
  gray70,
  gray80,
  gray90,
  green10,
  green100,
  pink,
  primary10,
  primaryColor,
  tertiaryButtonOutlinedColor,
  yellow
} from '../../../constants/Colors';
import halfStar from '../../../components/assets/HalfMaskedStar.svg';
import {
  certificationFlgOptions,
  productMethodList
} from '../../../constants/SelectOptions';
import { AuthContext } from '../../../contexts/auth';
import { ProductDetailType } from '../../../validation/products/productType';
import {
  EvaluationGetType,
  EvaluationType
} from '../../../validation/evaluations/evaluationsType';
import { common } from '../../../modules/error';

import noImage from '../../assets/noImage.svg';
import CustomDialog from '../../uiParts/CustomDialog';
import CustomSelectField from '../../uiParts/CustomSelectField';
import CustomPagination from '../../uiParts/CustomPagination';
import SideBar from '../../uiParts/SideBar';
import CustomLoading from '../../uiParts/CustomLoading';
import { addMylist, deleteMylistProduct } from '../../../api/db/mylist';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';

const styles = {
  container: {
    '@media screen and (min-width:961px)': {
      pl: sideBarWidth,
      m: '32px'
    },
    '@media screen and (max-width:960px)': {
      mt: spHeaderHeight,
      m: '24px 16px'
    }
  },
  mainImage: {
    width: '100%'
  },
  mainImageContainer: {
    width: '100%',
    position: 'relative'
  },
  subImageContainer: {
    width: '25%'
  },
  backImageButton: {
    position: 'absolute',
    top: '50%',
    left: '16px',
    transform: 'translateY(-50%)',
    color: 'white',
    bgcolor: gray100,
    opacity: '0.6',
    width: '36px',
    '&:hover': {
      bgcolor: gray100,
      opacity: '0.6'
    }
  },
  nextImageButton: {
    position: 'absolute',
    top: '50%',
    right: '16px',
    transform: 'translateY(-50%)',
    color: 'white',
    bgcolor: gray100,
    opacity: '0.6',
    width: '36px',
    '&:hover': {
      bgcolor: gray100,
      opacity: '0.6'
    }
  },
  aliasText: {
    color: gray100,
    fontSize: '14px',
    '@media screen and (max-width: 960px)': {
      fontSize: '12px'
    }
  },
  categoryText: {
    fontSize: '14px',
    color: gray90,
    '@media screen and (max-width: 960px)': {
      fontSize: '12px'
    }
  },
  nameText: {
    fontSize: '24px',
    color: gray100,
    fontWeight: 700,
    '@media screen and (max-width: 960px)': {
      fontSize: '22px'
    }
  },
  specificationText: {
    fontSize: '18px',
    color: gray100,
    fontWeight: 500,
    ml: '8px',
    mt: '7px',
    '@media screen and (max-width: 960px)': {
      fontSize: '16px'
    }
  },
  summaryText: {
    fontSize: '14px',
    color: gray100,
    mt: '8px'
  },
  unitText: {
    fontSize: '16px',
    color: gray100,
    mr: '8px',
    mt: '10px',
    '@media screen and (max-width: 960px)': {
      fontSize: '14px'
    }
  },
  valueText: {
    fontSize: '28px',
    color: primaryColor,
    fontWeight: 700,
    mr: '4px',
    '@media screen and (max-width: 960px)': {
      fontSize: '24px'
    }
  },
  guideMark: {
    color: gray90,
    cursor: 'pointer',
    height: '24px',
    width: '24px'
  },
  buttonIcon: {
    mr: '8px',
    height: '24px',
    width: '24px'
  },
  buttonText: {
    color: gray100,
    fontSize: '16px',
    fontWeight: 500
  },
  tableItem: {
    bgcolor: gray10,
    width: '30%'
  },
  tableValue: {
    color: gray100
  },
  tableText: {
    '@media screen and (max-width: 600px)': {
      fontSize: '12px'
    }
  },
  basisChip: {
    bgcolor: gray10,
    borderRadius: '0px',
    height: '24px'
  },
  basisActiveChip: {
    color: gray100
  },
  basisInactiveChip: {
    color: gray80
  },
  tableTitleCotainer: {
    p: '24px 0px',
    '@media screen and (max-width: 960px)': {
      p: '17px 0px'
    }
  },
  tableTitle: {
    fontSize: '18px',
    color: gray100,
    fontWeight: 500,
    '@media screen and (max-width: 960px)': {
      fontSize: '16px'
    }
  },
  updatedAt: {
    fontSize: '12px',
    color: gray90
  },
  myListNum: {
    alignItems: 'center',
    color: gray100,
    display: 'flex',
    fontSize: '14px',
    justifyContent: 'center',
    '@media screen and (max-width: 960px)': {
      fontSize: '12px'
    }
  },
  scoreText: {
    alignItems: 'center',
    color: gray100,
    display: 'flex',
    fontSize: '14px',
    justifyContent: 'center',
    '@media screen and (max-width: 960px)': {
      fontSize: '12px'
    }
  },
  halfStar: {
    height: '24px',
    width: '24px'
  },
  recommendChip: {
    bgcolor: primary10,
    color: primaryColor,
    fontSize: '12px',
    border: '1px solid' + primaryColor,
    height: '24px',
    '@media screen and (min-width: 961px)': {
      ml: '16px'
    }
  },
  heading: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: primary10,
    height: '42px',
    pl: '15px',
    fontSize: '18px',
    fontWeight: 500,
    color: gray100,
    borderLeft: `3px solid ${primaryColor}`,
    mb: '16px'
  },
  noneDisplayOnPC: {
    '@media screen and (min-width: 961px)': {
      display: 'none'
    }
  },
  noneDisplayOnSP: {
    '@media screen and (max-width: 960px)': {
      display: 'none'
    }
  },
  buttonContainer: {
    '@media screen and (min-width:961px)': {
      display: 'none'
    },
    backgroundColor: 'rgba(255, 255, 255, 0.80)',
    p: '12px 16px',
    position: 'sticky',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 2,
    display: 'flex',
    justifyContent: 'space-between'
  },
  co2ValueIcon: {
    color: primaryColor,
    height: '20px',
    width: '20px',
    verticalAlign: 'bottom'
  },
  co2ValueItem: {
    color: gray100,
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '14px',
    mr: '4px',
    verticalAlign: 'bottom'
  },
  co2ValueUnit: {
    color: gray100,
    fontSize: '14px',
    lineHeight: '14px',
    verticalAlign: 'bottom'
  },
  co2Value: {
    color: primaryColor,
    fontSize: '26px',
    fontWeight: 700,
    lineHeight: '26px',
    mr: '4px',
    verticalAlign: 'bottom',
    '@media screen and (max-width: 600px)': {
      fontSize: '24px'
    }
  },
  convValueChip: {
    bgcolor: primary10,
    color: primaryColor,
    fontSize: '12px',
    fontWeight: '500',
    height: '21px',
    mr: '8px'
  },
  calcValueChip: {
    bgcolor: green10,
    color: green100,
    fontSize: '12px',
    fontWeight: '500',
    height: '21px',
    mr: '8px'
  }
};

// 評価を非表示
const showEvaluation: boolean = false;

const MaterialDetail = () => {
  const { id } = useParams();
  const [productData, setProductData] = useState<ProductDetailType>({
    alias: '',
    calculatedEmissionIntensity: '',
    calculatedMethod: null,
    calculatedReduceRate: 0,
    calculationBasisFile: {
      name: '',
      path: ''
    },
    calculationBasisName: '',
    calculationBasisType: 0,
    calculationBasisUrl: '',
    carbonOffsetFlg: false,
    calculationSupplement: '',
    category: '',
    condition: '',
    createdAt: '',
    document: {
      name: '',
      path: ''
    },
    documentDescriptionUrl: '',
    emissionIntensity: 0,
    evaluationScore: 0,
    favoriteFlg: 0,
    image: [
      {
        id: 0,
        name: '',
        path: '',
        sort: 0
      }
    ],
    material: '',
    co2Unit: '',
    costUnit: '',
    calculationSpecification: '',
    myListFlg: 0,
    myListNumber: 0,
    name: '',
    otherCertification: '',
    priceHistories: {
      productInfoId: 0,
      settingAt: '',
      referenceUnitPrice: 0,
      unit: ''
    },
    productInfoId: 0,
    recommendFlg: 0,
    reduceRate: 0,
    priceCondition: '',
    referenceUnitPrice: 0,
    relatedUrl: '',
    remark: '',
    priceRemark: '',
    settingAt: '',
    specification: '',
    summary: '',
    updatedAt: '',
    users: {
      company: '',
      companyKana: '',
      companyShort: '',
      department: '',
      email: '',
      firstName: '',
      firstNameKana: '',
      homepage: '',
      industry: '',
      lastName: '',
      lastNameKana: '',
      tel: '',
      certificationFlg: 0,
      certificationName: ''
    }
  });

  // ログイン管理
  const {
    checkLoginStatus,
    setErrorText,
    setInfoText,
    setSnackbarOpen,
    userId,
    userRank,
    userType
  } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    void (async () => {
      window.scrollTo(0, 0);
      if (!checkLoginStatus) return navigate('/auth/login');
      const result = await checkLoginStatus();
      if (!result.check) {
        return navigate('/auth/login');
      }
    })();
  }, []);

  // productInfoIdの取得
  const { state } = useLocation();
  const productInfoId = state?.productInfoId ?? null;

  const [selectedMethod, setSelectedMethod] = useState<number>(1);
  const handleChangeMethod = (event: SelectChangeEvent<unknown>) => {
    setSelectedMethod(event.target.value as number);

    window.dataLayer.push({
      event: 'co2_method',
      screen: window.location.pathname,
      category: 'button',
      action: 'material_detail_co2_method',
      label: productData.name,
      value: productData.id,
      user_id: userId,
      user_type: userType,
      user_rank: userRank
    });
  };

  const dateFormatter = (date: Date) => {
    const newDate = new Date(date);
    return newDate.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const [loading, setLoading] = useState(false);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [isMylist, setIsMylist] = useState<boolean>(false);
  const [mylistNum, setMylistNum] = useState(0);

  const fetchProduct = async () => {
    const res = await getProduct(Number(id), selectedMethod, productInfoId);
    const productData = res.data.products;
    setProductData({
      alias: productData.alias,
      calculatedEmissionIntensity: productData.calculated_emission_intensity,
      calculatedMethod: productData.calculated_method,
      calculatedReduceRate: productData.calculated_reduce_rate,
      calculationBasisFile: {
        name: productData.calculation_basis_file.name,
        path: productData.calculation_basis_file.path
      },
      calculationBasisName: productData.calculation_basis_name,
      calculationBasisType: productData.calculation_basis_type,
      calculationBasisUrl: productData.calculation_basis_url,
      carbonOffsetFlg: productData.carbon_offset_flg,
      calculationSupplement: productData.calculation_supplement,
      category: productData.category.name,
      condition: productData.condition,
      createdAt: productData.created_at,
      document: {
        name: productData.document.name,
        path: productData.document.path
      },
      documentDescriptionUrl: productData.document_description_url,
      emissionIntensity: productData.emission_intensity,
      evaluationScore: productData.evaluation_score,
      favoriteFlg: productData.favorite_flg,
      image: productData.image,
      material: productData.material.name,
      co2Unit: productData.material.co2_unit,
      costUnit: productData.material.cost_unit,
      calculationSpecification: productData.material.calculation_specification,
      myListFlg: productData.mylist_flg,
      myListNumber: productData.mylist_number,
      name: productData.name,
      otherCertification: productData.other_certification,
      priceHistories: productData.price_histories,
      productInfoId: productData.product_info_id,
      recommendFlg: productData.recommend_flg,
      reduceRate: productData.reduce_rate,
      priceCondition: productData.material.price_condition,
      referenceUnitPrice: productData.reference_unit_price,
      relatedUrl: productData.related_url,
      remark: productData.remark,
      settingAt:
        productData.setting_at && dateFormatter(productData.setting_at),
      priceRemark: productData.price_remarks,
      specification: productData.specification,
      summary: productData.summary,
      updatedAt: dateFormatter(productData.updated_at),
      users: {
        company: productData.users.company,
        companyKana: productData.users.company_kana,
        companyShort: productData.users.company_short,
        department: productData.users.department,
        email: productData.users.email,
        firstName: productData.users.first_name,
        firstNameKana: productData.users.first_name_kana,
        homepage: productData.users.homepage,
        industry: productData.users.industry,
        lastName: productData.users.last_name,
        lastNameKana: productData.users.last_name_kana,
        tel: productData.users.tel,
        certificationFlg: productData.users.certification_flg,
        certificationName: productData.users.certification_name
      }
    });
    setIsFavorite(Boolean(productData.favorite_flg));
    setIsMylist(Boolean(productData.mylist_flg));
    setMylistNum(productData.mylist_number);
  };

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        await fetchProduct();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          if (setErrorText && setSnackbarOpen) {
            setErrorText(errorResponse.data.message);
            setSnackbarOpen(true);
          }
        } else {
          if (setErrorText && setSnackbarOpen) {
            setErrorText(common);
            setSnackbarOpen(true);
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id, selectedMethod]);

  // 建材画像
  const sortedImageData = productData.image
    .sort((a, b) => (a?.sort || 0) - (b?.sort || 0))
    .filter((image) => image?.path);
  let images: string[] = [];
  if (sortedImageData[2]?.path) {
    images = [
      sortedImageData[0]?.path,
      sortedImageData[1]?.path,
      sortedImageData[2]?.path
    ];
  } else if (sortedImageData[1]?.path) {
    images = [sortedImageData[0]?.path, sortedImageData[1]?.path];
  } else if (sortedImageData[0]?.path) {
    images = [sortedImageData[0]?.path];
  } else {
    images = [noImage];
  }

  const [displayMainImageIndex, setDisplayMainImageIndex] = useState(0);

  const handleNextImage = (): void => {
    const newValue = (displayMainImageIndex + 1) % images.length;
    setDisplayMainImageIndex(newValue);
    return;
  };

  const handleBackImage = (): void => {
    let newValue = (displayMainImageIndex - 1) % images.length;
    if (newValue < 0) {
      newValue = 1;
    }
    setDisplayMainImageIndex(newValue);
    return;
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleDialogOpen = () => {
    setDialogOpen(!dialogOpen);
    window.dataLayer.push({
      event: 'click',
      screen: window.location.pathname,
      category: 'icon',
      action: 'material_detail_help',
      label: productData.name,
      value: productData.id,
      user_id: userId,
      user_type: userType,
      user_rank: userRank
    });
  };

  // マイリストボタン
  const MylistButton: React.FC = () => {
    const toggleMylist = async () => {
      try {
        if (!id) return;
        if (isMylist) {
          const res = await deleteMylistProduct(Number(id));
          setInfoText &&
            setInfoText(`マイリスト「${res.data.title}」から削除しました`);
          setIsMylist(false);
          window.dataLayer.push({
            event: 'mylist_del',
            screen: window.location.pathname,
            category: 'button',
            action: 'material_detail_mylist_del',
            label: productData.name,
            value: productData.id,
            user_id: userId,
            user_type: userType,
            user_rank: userRank
          });
          await fetchProduct();
        } else {
          const res = await addMylist(Number(id));
          setInfoText &&
            setInfoText(`マイリスト「${res.data.title}」に追加しました`);
          setIsMylist(true);
          window.dataLayer.push({
            event: 'mylist',
            screen: window.location.pathname,
            category: 'button',
            action: 'material_detail_mylist',
            label: productData.name,
            value: productData.productInfoId,
            user_id: userId,
            user_type: userType,
            user_rank: userRank
          });
          await fetchProduct();
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          setErrorText && setErrorText(errorResponse.data.message);
        } else {
          setErrorText && setErrorText(common);
        }
      } finally {
        setSnackbarOpen && setSnackbarOpen(true);
      }
    };

    return (
      <Button
        sx={[tertiaryButtonOutlinedColor, { height: '44px' }]}
        fullWidth
        onClick={toggleMylist}
      >
        {isMylist ? (
          <>
            <AssignmentIcon
              sx={{
                ...styles.buttonIcon,
                ...{ color: primaryColor }
              }}
            />
            <Typography sx={styles.buttonText}>マイリストに追加済み</Typography>
          </>
        ) : (
          <>
            <AssignmentOutlined
              sx={{
                ...styles.buttonIcon,
                ...{ color: gray90 }
              }}
            />
            <Typography sx={styles.buttonText}>マイリストに追加</Typography>
          </>
        )}
      </Button>
    );
  };

  // お気に入りボタン
  const FavoriteButton: React.FC = () => {
    const toggleFavorite = async () => {
      try {
        if (!id) return;
        if (isFavorite) {
          await deleteFavorite(Number(id));
          setInfoText && setInfoText('お気に入りから削除しました');
          setIsFavorite(false);
          window.dataLayer.push({
            event: 'favorite_del',
            screen: window.location.pathname,
            category: 'button',
            action: 'material_detail_favorite_del',
            label: productData.name,
            value: productData.id,
            user_id: userId,
            user_type: userType,
            user_rank: userRank
          });
        } else {
          await addFavorite(Number(id));
          setInfoText && setInfoText('お気に入りに追加しました');
          setIsFavorite(true);
          window.dataLayer.push({
            event: 'favorite',
            screen: window.location.pathname,
            category: 'button',
            action: 'material_detail_favorite',
            label: productData.name,
            value: productData.productInfoId,
            user_id: userId,
            user_type: userType,
            user_rank: userRank
          });
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          setErrorText && setErrorText(errorResponse.data.message);
        } else {
          setErrorText && setErrorText(common);
        }
      } finally {
        setSnackbarOpen && setSnackbarOpen(true);
      }
    };

    return (
      <>
        <Button
          sx={[
            tertiaryButtonOutlinedColor,
            styles.noneDisplayOnSP,
            { height: '44px' }
          ]}
          fullWidth
          onClick={toggleFavorite}
        >
          {isFavorite ? (
            <>
              <FavoriteOutlinedIcon
                sx={{
                  ...styles.buttonIcon,
                  ...{ color: pink }
                }}
              />
              <Typography sx={styles.buttonText}>
                お気に入りに追加済み
              </Typography>
            </>
          ) : (
            <>
              <FavoriteBorderOutlined
                sx={{
                  ...styles.buttonIcon
                }}
              />
              <Typography sx={styles.buttonText}>お気に入りに追加</Typography>
            </>
          )}
        </Button>
        <IconButton sx={styles.noneDisplayOnPC} onClick={toggleFavorite}>
          {isFavorite ? (
            <FavoriteOutlinedIcon
              sx={{ width: '32px', height: '32px', color: pink }}
            />
          ) : (
            <FavoriteBorderOutlined sx={{ width: '32px', height: '32px' }} />
          )}
        </IconButton>
      </>
    );
  };

  const Rating = (props: { rating: number }) => {
    const { rating } = props;

    const starIcons = new Array(5).fill('outlined').map((_, index) => {
      if (rating - index >= 1) {
        return 'filled';
      } else if (rating - index >= 0.5) {
        return 'half-filled';
      }
      return 'outlined';
    });

    return (
      <Typography
        component="div"
        sx={{
          display: 'flex',
          mr: '4px',
          '@media screen and (max-width: 600px)': {
            height: '20px',
            alignItems: 'center'
          }
        }}
      >
        {starIcons.map((iconType, index) => (
          <Star type={iconType} key={index} />
        ))}
      </Typography>
    );
  };

  type StarProps = {
    type: 'filled' | 'half-filled' | 'outlined';
  };

  const Star = (props: StarProps) => {
    const { type } = props;
    switch (type) {
      case 'filled':
        return (
          <StarOutlinedIcon
            sx={{
              color: yellow,
              '@media screen and (max-width: 600px)': { width: '20px' }
            }}
          />
        );
      case 'half-filled':
        return (
          <CardMedia component="img" image={halfStar} sx={styles.halfStar} />
        );
      case 'outlined':
        return (
          <StarBorderOutlinedIcon
            sx={{
              color: gray80,
              '@media screen and (max-width: 600px)': { width: '20px' }
            }}
          />
        );
      default:
        return (
          <StarBorderOutlinedIcon
            sx={{
              color: gray80,
              '@media screen and (max-width: 600px)': { width: '20px' }
            }}
          />
        );
    }
  };

  // 建材口コミの取得
  const [evaluationList, setEvaluationList] = useState<EvaluationType[]>([]);
  const [evaluationCurrentPage, setEvaluationCurrentPage] = useState(1);
  const limit = 20;
  const [totalPages, setTotalPages] = useState(1);
  const topRef = useRef<HTMLDivElement>(null);

  const evaluationDateFormatter = (date: Date) => {
    const newDate = new Date(date);
    return (
      newDate.getFullYear() +
      '/' +
      String(newDate.getMonth() + 1).padStart(2, '0') +
      '/' +
      String(newDate.getDate()).padStart(2, '0')
    );
  };

  // 口コミ最初の20件取得
  useEffect(() => {
    void (async () => {
      try {
        // offsetの設定
        const offset = (evaluationCurrentPage - 1) * limit;
        // 口コミの取得
        const res = await getProductEvaluations(Number(id), limit, offset);
        if (res.data.total_count === 0) return;
        const evaluations = res.data.evaluations;
        // 口コミがない場合、何もしない
        if (!evaluations) return;
        // 口コミがある場合、データを整形してセット
        setTotalPages(Math.ceil(res.data.total_count / limit));
        const newDataList = evaluations.map((evaluation: EvaluationGetType) => {
          return {
            id: evaluation.id,
            score: evaluation.score,
            nickname: evaluation.nickname,
            title: evaluation.title,
            content: evaluation.content,
            operatorFlg: evaluation.operator_flg,
            createdAt: evaluationDateFormatter(evaluation.created_at),
            updatedAt: evaluationDateFormatter(evaluation.updated_at)
          };
        });
        setEvaluationList([...evaluationList, ...newDataList]);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          if (setErrorText && setSnackbarOpen) {
            setErrorText(errorResponse.data.message);
            setSnackbarOpen(true);
          }
        } else {
          if (setErrorText && setSnackbarOpen) {
            setErrorText(common);
            setSnackbarOpen(true);
          }
        }
      }
    })();
  }, [id]);

  // ページ変更による再取得
  const setListPagination = async (page: number) => {
    try {
      setLoading(true);
      setEvaluationCurrentPage(page);
      const res = await getProductEvaluations(
        Number(id),
        limit,
        (page - 1) * limit
      );
      const evaluations = res.data.evaluations;
      const newDataList = evaluations.map((evaluation: EvaluationGetType) => {
        return {
          id: evaluation.id,
          score: evaluation.score,
          nickname: evaluation.nickname,
          title: evaluation.title,
          content: evaluation.content,
          operatorFlg: evaluation.operator_flg,
          createdAt: evaluationDateFormatter(evaluation.created_at),
          updatedAt: evaluationDateFormatter(evaluation.updated_at)
        };
      });
      setEvaluationList(newDataList);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      if (setErrorText && setSnackbarOpen) {
        setSnackbarOpen(true);
        setErrorText('データの取得に失敗しました。');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomHead title={`${productData.name} | CMCOS[コムコス]`} />
      <CustomLoading loading={loading} />
      <Grid container>
        <SideBar />
        <Grid sx={styles.container} container>
          <Box width="100%" sx={styles.noneDisplayOnPC}>
            <CustomSelectField
              list={productMethodList}
              onChange={handleChangeMethod}
              value={selectedMethod}
              type={'primary'}
            />
          </Box>
          <Grid
            className="imageContainer"
            item
            md={6}
            xs={12}
            sx={{
              pr: '32px',
              '@media screen and (max-width: 900px)': {
                pr: 0,
                mt: '24px'
              }
            }}
          >
            <Box sx={styles.mainImageContainer}>
              <CardMedia
                component="img"
                src={images[displayMainImageIndex]}
                sx={styles.mainImage}
              />
              {!images[1] ? (
                <></>
              ) : (
                <>
                  <IconButton
                    sx={styles.backImageButton}
                    onClick={handleBackImage}
                  >
                    <KeyboardArrowLeft width="24px" height="24px" />
                  </IconButton>
                  <IconButton
                    sx={styles.nextImageButton}
                    onClick={handleNextImage}
                  >
                    <KeyboardArrowRight width="24px" height="24px" />
                  </IconButton>
                </>
              )}
            </Box>
            <Grid container sx={{ width: '100%' }} justifyContent="center">
              {!images[1] ? (
                <></>
              ) : (
                <MobileStepper
                  variant="dots"
                  steps={!images[2] ? 2 : 3}
                  nextButton={null}
                  backButton={null}
                  activeStep={displayMainImageIndex}
                  position="static"
                  sx={[
                    {
                      width: '40px'
                    },
                    styles.noneDisplayOnPC
                  ]}
                />
              )}
            </Grid>
            {!images[1] ? (
              <></>
            ) : (
              <Grid container mt="8px" sx={styles.noneDisplayOnSP}>
                <CardMedia
                  component="img"
                  src={images[(displayMainImageIndex + 1) % images.length]}
                  sx={styles.subImageContainer}
                  onClick={() =>
                    setDisplayMainImageIndex(
                      (displayMainImageIndex + 1) % images.length
                    )
                  }
                />
                {!images[2] ? (
                  <></>
                ) : (
                  <CardMedia
                    component="img"
                    src={images[(displayMainImageIndex + 2) % images.length]}
                    sx={styles.subImageContainer}
                    onClick={() =>
                      setDisplayMainImageIndex(
                        (displayMainImageIndex + 2) % images.length
                      )
                    }
                  />
                )}
              </Grid>
            )}
          </Grid>
          <Grid
            item
            md={6}
            xs={12}
            sx={{
              '@media screen and (max-width: 960px)': {
                mt: '16px'
              }
            }}
          >
            <Grid width="100%" container>
              <Grid
                container
                width="100%"
                height="44px"
                justifyContent="flex-end"
                mb="24px"
                sx={styles.noneDisplayOnSP}
              >
                <Box width="200px">
                  <CustomSelectField
                    list={productMethodList}
                    onChange={handleChangeMethod}
                    value={selectedMethod}
                    type={'primary'}
                  />
                </Box>
              </Grid>
              <Grid container justifyContent="space-between">
                <Typography sx={styles.aliasText}>
                  {productData.alias}
                </Typography>
                <Typography sx={styles.categoryText}>
                  {productData.category ? productData.category : ''}
                  {productData.category && productData.material ? ' / ' : ''}
                  {productData.category && productData.material
                    ? productData.material.length > 12
                      ? productData.material.slice(0, 12) + '...'
                      : productData.material
                    : ''}
                </Typography>
              </Grid>
              <Grid container>
                <Typography sx={styles.nameText}>{productData.name}</Typography>
                <Typography sx={styles.specificationText}>
                  {productData.specification}
                </Typography>
              </Grid>
              <Grid container>
                <Typography sx={styles.summaryText}>
                  {productData.summary}
                </Typography>
              </Grid>
              <Grid
                container
                mt="16px"
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid item flexGrow={1}>
                  <Grid
                    container
                    flexDirection={'row'}
                    flexWrap={'nowrap'}
                    justifyContent={'space-between'}
                    alignItems={'flex-end'}
                    columnGap={'24px'}
                    mb={'12px'}
                  >
                    <Grid
                      item
                      container
                      flexDirection={{ xs: 'column', sm: 'row' }}
                      columnGap={'4px'}
                      rowGap={'5px'}
                    >
                      <Box>
                        <Typography component="span" sx={styles.co2ValueItem}>
                          CO2原単位
                        </Typography>
                        {productData.calculatedMethod === 1 ? (
                          <Chip label="換算値" sx={[styles.convValueChip]} />
                        ) : productData.calculatedMethod === 2 ? (
                          <Chip label="算定値" sx={[styles.calcValueChip]} />
                        ) : (
                          <></>
                        )}
                        <Typography component="span" sx={styles.co2Value}>
                          {productData.calculatedEmissionIntensity !== null
                            ? productData.calculatedEmissionIntensity
                            : 'ー'}
                        </Typography>
                        <Typography
                          component="span"
                          sx={[styles.co2ValueUnit, { mr: '24px' }]}
                        >
                          kg-CO2/{productData.co2Unit}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography component="span" sx={styles.co2ValueItem}>
                          CO2削減率
                        </Typography>
                        {productData.calculatedReduceRate !== null &&
                        productData.calculatedReduceRate !== undefined ? (
                          <>
                            <ChangeHistoryOutlinedIcon
                              sx={styles.co2ValueIcon}
                            />
                            <Typography component="span" sx={styles.co2Value}>
                              {productData.calculatedReduceRate}
                            </Typography>
                          </>
                        ) : (
                          <Typography component="span" sx={styles.co2Value}>
                            ー
                          </Typography>
                        )}
                        <Typography component="span" sx={styles.co2ValueUnit}>
                          %
                        </Typography>
                      </Box>
                    </Grid>
                    <Box ml={'auto'}>
                      <HelpOutlineOutlinedIcon
                        sx={styles.guideMark}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDialogOpen();
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              width="100%"
              justifyContent="space-between"
              mt="24px"
              sx={styles.noneDisplayOnSP}
            >
              <Grid item width="63%">
                <MylistButton />
              </Grid>
              <Grid item width="35%">
                <FavoriteButton />
              </Grid>
            </Grid>
            <Grid container width="100%" mt="25px">
              <Grid item width="100%">
                {productData.recommendFlg ? (
                  <Chip
                    label="おすすめ"
                    sx={[styles.recommendChip, styles.noneDisplayOnPC]}
                  />
                ) : (
                  <></>
                )}
              </Grid>
              <Typography sx={styles.myListNum}>
                マイリスト追加数：{mylistNum}
              </Typography>
              <Divider
                orientation="vertical"
                sx={{ width: '1px', height: '24px', m: '0 12px' }}
              />
              <Typography sx={styles.scoreText}>
                評価：
                <Rating rating={productData.evaluationScore} />
                <Typography component="span" sx={styles.scoreText}>
                  {productData.evaluationScore}
                </Typography>
              </Typography>
              {productData.recommendFlg ? (
                <Chip
                  label="おすすめ"
                  sx={[styles.recommendChip, styles.noneDisplayOnSP]}
                />
              ) : (
                <></>
              )}
            </Grid>
            {/* material info table */}
            <Grid
              container
              width="100%"
              sx={styles.tableTitleCotainer}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography sx={styles.tableTitle}>建材の情報</Typography>
              <Typography sx={styles.updatedAt}>
                最終更新日：{productData.updatedAt}
              </Typography>
            </Grid>
            <TableContainer>
              <Table>
                <TableBody sx={{ border: '1px solid' + gray70 }}>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>適用条件</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.condition}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        メーカー算定値
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.emissionIntensity !== null
                          ? productData.emissionIntensity.toLocaleString() + ' '
                          : 'ー '}
                        kg-CO2/{productData.co2Unit}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        基準CO2算定仕様
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.calculationSpecification}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>CO2算定根拠</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Grid container flexDirection="column">
                        {/* chips */}
                        <Grid item>
                          <Grid
                            container
                            flexDirection={'row'}
                            gap={'8px'}
                            mb={'4px'}
                          >
                            <Chip
                              icon={
                                productData.calculationBasisType === 1 ? (
                                  <CheckOutlinedIcon />
                                ) : undefined
                              }
                              label="第三者認証"
                              sx={{
                                ...styles.basisChip,
                                ...(productData.calculationBasisType === 1
                                  ? styles.basisActiveChip
                                  : styles.basisInactiveChip)
                              }}
                            />
                            <Chip
                              icon={
                                productData.calculationBasisType === 2 ? (
                                  <CheckOutlinedIcon />
                                ) : undefined
                              }
                              label="自社算定"
                              sx={{
                                ...styles.basisChip,
                                ...(productData.calculationBasisType === 2
                                  ? styles.basisActiveChip
                                  : styles.basisInactiveChip)
                              }}
                            />
                            <Chip
                              icon={
                                productData.calculationBasisType === 3 ? (
                                  <CheckOutlinedIcon />
                                ) : undefined
                              }
                              label="その他"
                              sx={{
                                ...styles.basisChip,
                                ...(productData.calculationBasisType === 3
                                  ? styles.basisActiveChip
                                  : styles.basisInactiveChip)
                              }}
                            />
                          </Grid>
                        </Grid>
                        <Grid item mt={'7px'}>
                          <Typography color={gray100} sx={styles.tableText}>
                            {productData.calculationBasisName}
                          </Typography>
                        </Grid>
                        <Grid item mt={'4px'}>
                          <Grid container>
                            <Typography color={gray100} sx={styles.tableText}>
                              ファイル：
                            </Typography>
                            <Box
                              component={'a'}
                              href={productData.calculationBasisFile?.path}
                              download
                              color={primaryColor}
                              sx={styles.tableText}
                              target="_blank"
                            >
                              {productData.calculationBasisFile?.name}
                            </Box>
                          </Grid>
                        </Grid>
                        <Grid item mt={'4px'}>
                          <Grid container>
                            <Typography color={gray100} sx={styles.tableText}>
                              URL：
                            </Typography>
                            <Box
                              component={'a'}
                              href={productData.calculationBasisUrl}
                              color={primaryColor}
                              sx={styles.tableText}
                              target="_blank"
                            >
                              {productData.calculationBasisUrl}
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        CO2算定の補足
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.calculationSupplement &&
                          (productData.calculationSupplement.includes('\n') ? (
                            productData.calculationSupplement
                              .split('\n')
                              .map((line, index) => (
                                <span key={index}>
                                  {line}
                                  <br />
                                </span>
                              ))
                          ) : (
                            <span>{productData.calculationSupplement}</span>
                          ))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        カーボンオフセットの利用
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.carbonOffsetFlg ? 'あり' : 'なし'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        その他の認定情報
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.otherCertification &&
                          (productData.otherCertification.includes('\n') ? (
                            productData.otherCertification
                              .split('\n')
                              .map((line, index) => (
                                <span key={index}>
                                  {line}
                                  <br />
                                </span>
                              ))
                          ) : (
                            <span>{productData.otherCertification}</span>
                          ))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        補足説明資料
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Grid item mt={'4px'}>
                        <Grid container>
                          <Typography color={gray100} sx={styles.tableText}>
                            ファイル：
                          </Typography>
                          <Box
                            component={'a'}
                            href={productData.document?.path}
                            download
                            color={primaryColor}
                            sx={styles.tableText}
                            target="_blank"
                          >
                            {productData.document?.name}
                          </Box>
                        </Grid>
                        <Grid item mt={'4px'}>
                          <Grid container>
                            <Typography color={gray100} sx={styles.tableText}>
                              URL：
                            </Typography>
                            <Box
                              component={'a'}
                              href={productData.documentDescriptionUrl}
                              color={primaryColor}
                              sx={styles.tableText}
                              target="_blank"
                            >
                              {productData.documentDescriptionUrl}
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer sx={{ mt: '24px' }}>
              <Table>
                <TableBody sx={{ border: '1px solid' + gray70 }}>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        単価算定条件
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.priceCondition}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        参考設計単価
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.referenceUnitPrice}
                        円/{productData.costUnit}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>単価設定日</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.settingAt}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        単価補足説明
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.priceRemark &&
                          (productData.priceRemark.includes('\n') ? (
                            productData.priceRemark
                              .split('\n')
                              .map((line, index) => (
                                <span key={index}>
                                  {line}
                                  <br />
                                </span>
                              ))
                          ) : (
                            <span>{productData.priceRemark}</span>
                          ))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer sx={{ mt: '24px', mb: '24px' }}>
              <Table>
                <TableBody sx={{ border: '1px solid' + gray70 }}>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>関連URL</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Box
                        component={'a'}
                        href={productData.relatedUrl}
                        color={primaryColor}
                        sx={styles.tableText}
                        target="_blank"
                      >
                        {productData.relatedUrl}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>その他備考</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.remark &&
                          (productData.remark.includes('\n') ? (
                            productData.remark
                              .split('\n')
                              .map((line, index) => (
                                <span key={index}>
                                  {line}
                                  <br />
                                </span>
                              ))
                          ) : (
                            <span>{productData.remark}</span>
                          ))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {/* subscriber info table */}
            <Grid
              container
              width="100%"
              sx={styles.tableTitleCotainer}
              alignItems="center"
            >
              <Typography sx={styles.tableTitle}>登録者の情報</Typography>
            </Grid>
            <TableContainer>
              <Table>
                <TableBody sx={{ border: '1px solid' + gray70 }}>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>会社名</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.users?.company}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>部署名</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.users?.department}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>担当者</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography
                        sx={styles.tableText}
                      >{`${productData.users?.lastName} ${productData.users?.firstName}`}</Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>電話番号</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.users?.tel}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        メールアドレス
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.users?.email}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>
                        企業の環境認定
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.users?.certificationFlg ===
                        certificationFlgOptions[1].value
                          ? '取得済'
                          : productData.users?.certificationFlg ===
                            certificationFlgOptions[0].value
                          ? '未取得'
                          : ''}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={styles.tableItem}>
                      <Typography sx={styles.tableText}>認定の名称</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableValue}>
                      <Typography sx={styles.tableText}>
                        {productData.users?.certificationName}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          {showEvaluation && (
            <Grid item xs={12} mt="64px" id="evaluationSection">
              <Box sx={styles.heading} ref={topRef}>
                建材の評価
              </Box>
              {evaluationList.length > 0 ? (
                evaluationList.map((evaluation, index) => {
                  if (evaluation.operatorFlg !== 1) {
                    return (
                      <Grid key={index}>
                        <Box p="24px 16px">
                          <Grid container alignItems="center">
                            <Rating rating={evaluation.score} />
                            <Typography
                              sx={{
                                fontSize: '14px',
                                color: gray100,
                                fontWeight: 500,
                                ml: '4px'
                              }}
                            >
                              {evaluation.score}
                            </Typography>
                          </Grid>
                          <Grid
                            container
                            sx={{ color: gray90, mt: '4px' }}
                            alignItems="center"
                          >
                            <Typography sx={{ fontSize: '14px' }}>
                              {evaluation.nickname}
                            </Typography>
                            <Divider
                              orientation="vertical"
                              sx={{ width: '1px', height: '16px', m: '0 8px' }}
                            />
                            <Typography sx={{ fontSize: '14px' }}>
                              {evaluation.updatedAt}
                            </Typography>
                          </Grid>
                          <Grid
                            container
                            sx={{ mt: '4px' }}
                            flexDirection="column"
                          >
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: '16px',
                                color: gray100
                              }}
                            >
                              {evaluation.title}
                            </Typography>
                            <Typography
                              sx={{ fontSize: '14px', color: gray100 }}
                            >
                              {evaluation.content}
                            </Typography>
                          </Grid>
                        </Box>
                        <Divider sx={{ width: '100%' }} />
                      </Grid>
                    );
                  } else {
                    return (
                      <Grid key={index}>
                        <Box p="24px 16px" sx={{ bgcolor: primary10 }}>
                          <Typography
                            sx={{
                              color: primaryColor,
                              fontSize: '16px',
                              fontWeight: 500
                            }}
                          >
                            事務局口コミ
                          </Typography>
                          <Grid
                            container
                            sx={{ color: gray90, mt: '12px' }}
                            alignItems="center"
                          >
                            <Typography sx={{ fontSize: '14px' }}>
                              {evaluation.updatedAt}
                            </Typography>
                          </Grid>
                          <Grid
                            container
                            sx={{ mt: '4px' }}
                            flexDirection="column"
                          >
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: '16px',
                                color: gray100
                              }}
                            >
                              {evaluation.title}
                            </Typography>
                            <Typography
                              sx={{ fontSize: '14px', color: gray100 }}
                            >
                              {evaluation.content}
                            </Typography>
                          </Grid>
                        </Box>
                        <Divider sx={{ width: '100%' }} />
                      </Grid>
                    );
                  }
                })
              ) : (
                <Grid
                  sx={{
                    m: '80px',
                    '@media screen and (max-width:961px)': {
                      m: '80px 0 120px'
                    }
                  }}
                >
                  <Typography
                    sx={{
                      color: gray90,
                      fontSize: '16px',
                      textAlign: 'center'
                    }}
                  >
                    評価コメントはまだありません
                  </Typography>
                </Grid>
              )}

              {totalPages > 1 && (
                <CustomPagination
                  currentPage={evaluationCurrentPage}
                  totalPages={totalPages}
                  handlePageChange={(e, page) => {
                    setListPagination(page);
                    topRef?.current?.scrollIntoView();
                  }}
                />
              )}
            </Grid>
          )}
        </Grid>
        <Grid sx={styles.buttonContainer} container flexDirection="row">
          <Box sx={{ width: 'calc(100% - 48px)' }}>
            <MylistButton />
          </Box>
          <FavoriteButton />
        </Grid>
        <CustomDialog
          onClose={(e) => {
            e.stopPropagation();
            handleDialogOpen();
          }}
          open={dialogOpen}
        />
      </Grid>
    </>
  );
};

export default MaterialDetail;
